import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  fetchClientCoordinatesThunk,
  fetchPvzListThunk
} from './pvzMapThunks';
import { type IMapInstanceState } from '@/types/IMapInstanceState';
import { type IPvzMapState } from '@/types/IPvzMapState';
import {
  type IInstanceIdPayload,
  type IRegisterMapInstancePayload,
  type ISetMapContextPayload,
  type ISetSelectedPvzPayload
} from '@/types/IPvzMapPayloads';

const createEmptyInstance = (): IMapInstanceState => ({
  pvzList: [],
  pvzListStatus: 'idle',
  pvzListError: null,
  selectedPvzId: null,
  clientCoordinates: null,
  clientCoordinatesStatus: 'idle',
  clientCoordinatesError: null,
  provider: null,
  city: null,
  clientAddress: null
});

const initialState: IPvzMapState = {
  instances: {}
};

const ensureInstance = (
  state: IPvzMapState,
  instanceId: string
): IMapInstanceState => {
  const existing = state.instances[instanceId];
  if (existing !== undefined) {
    return existing;
  }
  const created = createEmptyInstance();
  state.instances[instanceId] = created;
  return created;
};

const pvzMapSlice = createSlice({
  name: 'pvzMap',
  initialState,
  reducers: {
    registerMapInstance: (
      state,
      action: PayloadAction<IRegisterMapInstancePayload>
    ) => {
      const { instanceId, initial } = action.payload;
      if (state.instances[instanceId] === undefined) {
        state.instances[instanceId] = {
          ...createEmptyInstance(),
          ...initial
        };
      }
    },
    unregisterMapInstance: (
      state,
      action: PayloadAction<IInstanceIdPayload>
    ) => {
      const { instanceId } = action.payload;
      state.instances = Object.fromEntries(
        Object.entries(state.instances).filter(([key]) => key !== instanceId)
      );
    },
    resetMapInstance: (state, action: PayloadAction<IInstanceIdPayload>) => {
      if (state.instances[action.payload.instanceId] !== undefined) {
        state.instances[action.payload.instanceId] = createEmptyInstance();
      }
    },
    setSelectedPvz: (state, action: PayloadAction<ISetSelectedPvzPayload>) => {
      const instance = ensureInstance(state, action.payload.instanceId);
      instance.selectedPvzId = action.payload.pvzId;
    },
    setMapContext: (state, action: PayloadAction<ISetMapContextPayload>) => {
      const { instanceId, provider, city, clientAddress } = action.payload;
      const instance = ensureInstance(state, instanceId);
      if (provider !== undefined) {
        instance.provider = provider;
      }
      if (city !== undefined) {
        instance.city = city;
      }
      if (clientAddress !== undefined) {
        instance.clientAddress = clientAddress;
      }
    },
    clearPvzListError: (
      state,
      action: PayloadAction<IInstanceIdPayload>
    ) => {
      const instance = state.instances[action.payload.instanceId];
      if (instance !== undefined) {
        instance.pvzListError = null;
        if (instance.pvzListStatus === 'error') {
          instance.pvzListStatus = 'idle';
        }
      }
    },
    clearClientCoordinatesError: (
      state,
      action: PayloadAction<IInstanceIdPayload>
    ) => {
      const instance = state.instances[action.payload.instanceId];
      if (instance !== undefined) {
        instance.clientCoordinatesError = null;
        if (instance.clientCoordinatesStatus === 'error') {
          instance.clientCoordinatesStatus = 'idle';
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPvzListThunk.pending, (state, action) => {
        const instance = ensureInstance(state, action.meta.arg.instanceId);
        instance.pvzListStatus = 'loading';
        instance.pvzListError = null;
      })
      .addCase(fetchPvzListThunk.fulfilled, (state, action) => {
        const instance = ensureInstance(state, action.payload.instanceId);
        instance.pvzList = action.payload.pvzList;
        instance.pvzListStatus = 'ready';
        instance.pvzListError = null;
      })
      .addCase(fetchPvzListThunk.rejected, (state, action) => {
        const instanceId =
          action.payload?.instanceId ?? action.meta.arg.instanceId;
        const instance = ensureInstance(state, instanceId);
        if (action.meta.aborted) {
          instance.pvzListStatus = 'idle';
          return;
        }
        instance.pvzListStatus = 'error';
        instance.pvzListError =
          action.payload?.error.message ??
          'Не удалось загрузить список ПВЗ';
      })
      .addCase(fetchClientCoordinatesThunk.pending, (state, action) => {
        const instance = ensureInstance(state, action.meta.arg.instanceId);
        instance.clientCoordinatesStatus = 'loading';
        instance.clientCoordinatesError = null;
      })
      .addCase(fetchClientCoordinatesThunk.fulfilled, (state, action) => {
        const instance = ensureInstance(state, action.payload.instanceId);
        instance.clientCoordinates = action.payload.coordinates;
        instance.clientCoordinatesStatus = 'ready';
        instance.clientCoordinatesError = null;
      })
      .addCase(fetchClientCoordinatesThunk.rejected, (state, action) => {
        const instanceId =
          action.payload?.instanceId ?? action.meta.arg.instanceId;
        const instance = ensureInstance(state, instanceId);
        if (action.meta.aborted) {
          instance.clientCoordinatesStatus = 'idle';
          return;
        }
        instance.clientCoordinatesStatus = 'error';
        instance.clientCoordinatesError =
          action.payload?.error.message ??
          'Не удалось определить координаты клиента';
      });
  }
});

export const {
  registerMapInstance,
  unregisterMapInstance,
  resetMapInstance,
  setSelectedPvz,
  setMapContext,
  clearPvzListError,
  clearClientCoordinatesError
} = pvzMapSlice.actions;

export { fetchPvzListThunk, fetchClientCoordinatesThunk };

export default pvzMapSlice.reducer;
